import { getNestedBoolean } from '@/utils/configHook';
import { ConfigValues } from '@/utils/configManager';
import { FuseRouteItemType } from '@fuse/utils/FuseUtils';

/**
 * Configuration mapping for routes
 * Maps route IDs/paths to their corresponding configuration names and customizable property paths
 *
 * Example config structure for customization:
 * {
 *   "disablePage": false,
 *   "hiddenPage": true
 * }
 */
type RouteConfigMapping = {
    // Name of the config
    configName: string;
    // Whether a page should be disabled or not
    disablePath?: string;
    // Whether a page should be hidden from navigation but still accessible via URL
    hiddenPath?: string;
};

export const routeConfigMap: Record<string, RouteConfigMapping> = {
    account: {
        configName: 'account_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    mis: {
        configName: 'mis_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    billing: {
        configName: 'billing_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    webrar: {
        configName: 'webrar_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    zoneeditor: {
        configName: 'zone_editor_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    onboarding: {
        configName: 'onboarding_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    pricelist: {
        configName: 'pricelist_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },
    reports: {
        configName: 'mis_config',
        disablePath: 'disablePage',
        hiddenPath: 'hiddenPage'
    },

} as const;

/**
 * Apply configuration overrides to a single route
 * Sets hiddenPage property based on configuration
 *
 * @param route - The route to customize
 * @param config - The configuration values
 * @param configMapping - The configuration mapping for this route
 * @returns Route with applied customizations
 */
const applyConfigToRoute = (
    route: FuseRouteItemType,
    config: ConfigValues,
    configMapping: RouteConfigMapping
): FuseRouteItemType => {
    let updatedRoute: FuseRouteItemType = { ...route };

    // Apply hiddenPage if configured
    if (configMapping.hiddenPath) {
        const isHidden = getNestedBoolean(config, configMapping.hiddenPath, false);

        if (isHidden) {
            updatedRoute = {
                ...updatedRoute,
                hiddenPage: true
            };
        }
    }

    return updatedRoute;
};

/**
 * Apply configuration overrides to routes and their children
 * This function merges custom properties from config into the routes recursively
 *
 * @param routes - Array of routes to customize
 * @param configs - Object containing all loaded configurations
 * @returns Routes with applied customizations
 */
export const applyConfigToRoutes = (
    routes: FuseRouteItemType[],
    configs: Record<string, ConfigValues> = {}
): FuseRouteItemType[] => {
    try {
        return routes.map(route => {
            // Try to find a matching configuration mapping based on route path or id
            const routeKey = route.id || route.path?.split('/')[0];
            const configMapping = routeKey ? routeConfigMap[routeKey] : undefined;

            if (!configMapping || !configs[configMapping.configName]) {
                // No configuration mapping or config not loaded, return original route
                return route;
            }

            try {
                const config = configs[configMapping.configName];
                const customizedRoute = applyConfigToRoute(route, config, configMapping);

                // Recursively apply to children if they exist
                if (customizedRoute.children && customizedRoute.children.length > 0) {
                    customizedRoute.children = applyConfigToRoutes(customizedRoute.children, configs);
                }

                return customizedRoute;
            } catch (error) {
                console.warn(`Failed to apply config customizations to route ${route.path}:`, error);
                return route;
            }
        });
    } catch (error) {
        console.warn('Failed to apply config customizations to routes:: ', error);
        return routes;
    }
};

export default {
    applyConfigToRoutes
};
